import React, { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { motion } from 'framer-motion';
import { FaExchangeAlt, FaChevronDown, FaInfoCircle, FaChartLine } from 'react-icons/fa';
import TransferProgress from '../components/bridge/TransferProgress';
import TransactionHistory from '../components/bridge/TransactionHistory';
import ChainSelectModal from '../components/bridge/ChainSelectModal';
import { useCCTPTransfer, useUSDCBalance } from '../hooks/useBridge';
import { TRANSFER_STEPS } from '../config/cctp/constants';
import { SupportedChainId, CHAIN_TO_CHAIN_NAME } from '../config/cctp/chains';
import { getFormattedFinalityTime } from '../config/cctp/finality';
import { CHAIN_IMAGES } from '../config/chainImages';
import { calculateCCTPFees, CCTPFees, getFinalityThreshold, getEstimatedTime } from '../config/cctp/fees';
import BridgeApiService from '../services/bridgeApi';
import './Bridge.css';

const Bridge = () => {
  const { isConnected, address } = useAccount();
  const { openConnectModal } = useConnectModal();
  const [sourceChain, setSourceChain] = useState(SupportedChainId.ETHEREUM);
  const [destinationChain, setDestinationChain] = useState(SupportedChainId.AVALANCHE);
  const [amount, setAmount] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [showChainModal, setShowChainModal] = useState(false);
  const [chainModalType, setChainModalType] = useState('source'); // 'source' or 'destination'
  const [transferSpeed, setTransferSpeed] = useState('standard'); // 'standard' or 'fast'
  const [totalBridged, setTotalBridged] = useState({ displayAmount: '$0', totalTransactions: 0 });
  
  const { balance: sourceBalance, isLoading: sourceBalanceLoading, refetch: refetchSourceBalance } = useUSDCBalance(sourceChain);
  const { balance: destBalance, isLoading: destBalanceLoading, refetch: refetchDestBalance } = useUSDCBalance(destinationChain);
  const { 
    transfer, 
    transferStep, 
    error, 
    txHashes, 
    reset 
  } = useCCTPTransfer();

  // Modal handlers
  const openChainModal = (type) => {
    setChainModalType(type);
    setShowChainModal(true);
  };

  const closeChainModal = () => {
    setShowChainModal(false);
  };

  const handleChainSelect = (chainId) => {
    if (chainModalType === 'source') {
      // If selecting the current destination chain, swap them
      if (chainId === destinationChain) {
        const tempSource = sourceChain;
        setSourceChain(chainId);
        setDestinationChain(tempSource);
      } else {
        setSourceChain(chainId);
      }
    } else {
      // If selecting the current source chain, swap them
      if (chainId === sourceChain) {
        const tempDest = destinationChain;
        setDestinationChain(chainId);
        setSourceChain(tempDest);
      } else {
        setDestinationChain(chainId);
      }
    }
    closeChainModal();
  };

  // Fetch total bridged amount on mount and periodically
  useEffect(() => {
    const fetchTotalBridged = async () => {
      const result = await BridgeApiService.getTotalBridged();
      if (result.success) {
        setTotalBridged(result.data);
      }
    };

    fetchTotalBridged();
    const interval = setInterval(fetchTotalBridged, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (transferStep === TRANSFER_STEPS.COMPLETED) {
      setIsTransferring(false);
      // Refresh both source and destination balances after successful transfer
      refetchSourceBalance();
      refetchDestBalance();
      // Also refresh total bridged amount
      BridgeApiService.getTotalBridged().then(result => {
        if (result.success) {
          setTotalBridged(result.data);
        }
      });
      setTimeout(() => {
        reset();
        setAmount('');
      }, 5000);
    } else if (transferStep === TRANSFER_STEPS.ERROR) {
      setIsTransferring(false);
    } else if (transferStep === TRANSFER_STEPS.READY_TO_MINT) {
      // Also reset transferring state when ready to mint (manual process)
      setIsTransferring(false);
    }
  }, [transferStep, reset, refetchSourceBalance, refetchDestBalance]);

  const handleTransfer = async () => {
    if (!isConnected) {
      openConnectModal();
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (parseFloat(amount) > parseFloat(sourceBalance)) {
      alert('Insufficient USDC balance');
      return;
    }

    setIsTransferring(true);
    const isFast = transferSpeed === 'fast';
    await transfer(sourceChain, destinationChain, amount, isFast);
  };

  const handleMaxClick = () => {
    setAmount(sourceBalance);
  };

  const handleSwapChains = () => {
    const temp = sourceChain;
    setSourceChain(destinationChain);
    setDestinationChain(temp);
  };

  const handleMintComplete = () => {
    // Reset form after successful mint
    setAmount('');
    setIsTransferring(false);
    // Refresh both balances after mint completion
    refetchSourceBalance();
    refetchDestBalance();
    // Reset will be called after the success message displays
    setTimeout(() => {
      reset();
    }, 3000);
  };

  const isButtonDisabled = 
    isTransferring || 
    !amount || 
    parseFloat(amount) <= 0 || 
    parseFloat(amount) > parseFloat(sourceBalance);
  
  // Helper function to format number without trailing zeros
  const formatAmountNoTrailingZeros = (value) => {
    if (!value) return '';
    const formatted = parseFloat(value).toString();
    // Ensure we don't lose precision for very small amounts
    if (formatted.includes('e')) {
      return parseFloat(value).toFixed(6).replace(/\.?0+$/, '');
    }
    return formatted;
  };
  
  // Calculate fees based on amount and transfer speed
  const feeCalculation = amount && parseFloat(amount) > 0 
    ? calculateCCTPFees(
        (parseFloat(amount) * 1e6).toString(), // Convert to USDC units (6 decimals)
        sourceChain,
        transferSpeed === 'fast',
        destinationChain // Pass destination chain for platform fee calculation
      )
    : null;
    
  // Check if fast transfer is supported for current source chain
  const fastTransferSupported = CCTPFees.fastSupported[sourceChain];

  return (
    <motion.div 
      className="bridge-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="bridge-header">
        <div className="header-content">
          <div className="header-left">
            <h1>Bridge USDC</h1>
            <p>Transfer USDC between any supported chains using Circle's CCTP</p>
          </div>
          <div className="header-stats">
            <div className="stats-card">
              <div className="stats-icon">
                <FaChartLine />
              </div>
              <div className="stats-info">
                <span className="stats-label">Total Bridged</span>
                <span className="stats-value">{totalBridged.displayAmount}</span>
                <span className="stats-count">{totalBridged.totalTransactions.toLocaleString()} transactions</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bridge-container">
        <div className="bridge-main">
          <div className="bridge-card">
            <div className="card-header">
              <h2>Transfer</h2>
              <div className="info-icon-wrapper">
                <div className="info-icon">
                  <FaInfoCircle />
                </div>
                <div className="info-tooltip">
                  <div className="tooltip-content">
                    <h4>Cross-Chain USDC Bridge</h4>
                    
                    <p>This bridge uses Circle's Cross-Chain Transfer Protocol (CCTP) to enable secure, native USDC transfers between blockchains without wrapping or synthetic tokens.</p>
                    
                    <div className="tooltip-section">
                      <strong>How it works:</strong>
                      <ol>
                        <li>Approve USDC spending for the bridge contract</li>
                        <li>USDC is burned on the source chain</li>
                        <li>Circle validates and attests the burn transaction</li>
                        <li>Native USDC is minted on the destination chain</li>
                      </ol>
                    </div>
                    
                    <div className="tooltip-section">
                      <strong>Key Features:</strong>
                      <ul>
                        <li>Native USDC on all chains - no wrapped tokens</li>
                        <li>Secure attestation by Circle</li>
                        <li>Standard transfers: Chain-specific finality times</li>
                        <li>Fast transfers: ~8 seconds (0.01-0.14% fee on select chains)</li>
                        <li>No slippage or liquidity issues</li>
                        <li>1:1 value preservation</li>
                      </ul>
                    </div>
                    
                    <div className="tooltip-section">
                      <strong>Transfer Times:</strong>
                      <ul>
                        <li>Avalanche/Polygon/Sonic: ~8 seconds</li>
                        <li>Ethereum/Arbitrum/Base/Optimism: 13-19 minutes</li>
                        <li>Linea: 6-32 hours</li>
                        <li>Fast mode: ~8 seconds (where available)</li>
                      </ul>
                    </div>
                    
                    <p className="tooltip-footer">The bridge ensures your USDC maintains full backing and redeemability through Circle's infrastructure.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bridge-form">
              {/* Source Chain Input */}
              <div className="token-input-section">
                <div className="token-input-header">
                  <span className="input-label">From</span>
                  <div className="balance-with-max">
                    <button 
                      className="max-button"
                      onClick={handleMaxClick}
                      disabled={!sourceBalance || sourceBalanceLoading || isTransferring}
                    >
                      MAX
                    </button>
                    <span className="balance">
                      Balance: {sourceBalanceLoading ? 'Loading...' : `${parseFloat(sourceBalance || 0).toFixed(2)} USDC`}
                    </span>
                  </div>
                </div>
                <div className="token-input">
                  <input
                    type="number"
                    placeholder="0.0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="amount-input"
                    disabled={isTransferring}
                  />
                  <button 
                    className="chain-select"
                    onClick={() => openChainModal('source')}
                    disabled={isTransferring}
                  >
                    <div className="chain-info">
                      <img src={CHAIN_IMAGES[sourceChain]} alt={CHAIN_TO_CHAIN_NAME[sourceChain]} className="chain-icon" />
                      <span className="chain-name">{CHAIN_TO_CHAIN_NAME[sourceChain]}</span>
                      <span className="usdc-badge">USDC</span>
                    </div>
                    <FaChevronDown />
                  </button>
                </div>
              </div>

              {/* Swap Direction Button */}
              <div className="swap-direction">
                <button 
                  className="switch-btn" 
                  onClick={handleSwapChains}
                  disabled={isTransferring}
                  title="Swap chains"
                >
                  <FaExchangeAlt />
                </button>
              </div>

              {/* Destination Chain Input */}
              <div className="token-input-section">
                <div className="token-input-header">
                  <span className="input-label">To</span>
                  <span className="balance">
                    Balance: {destBalanceLoading ? 'Loading...' : `${parseFloat(destBalance || 0).toFixed(2)} USDC`}
                  </span>
                </div>
                <div className="token-input">
                  <input
                    type="number"
                    placeholder="0.0"
                    value={
                      feeCalculation && amount && parseFloat(amount) > 0
                        ? formatAmountNoTrailingZeros((parseFloat(feeCalculation.amountAfterAllFees) / 1e6).toFixed(6))
                        : amount || ''
                    }
                    readOnly
                    className="amount-input readonly"
                  />
                  <button 
                    className="chain-select"
                    onClick={() => openChainModal('destination')}
                    disabled={isTransferring}
                  >
                    <div className="chain-info">
                      <img src={CHAIN_IMAGES[destinationChain]} alt={CHAIN_TO_CHAIN_NAME[destinationChain]} className="chain-icon" />
                      <span className="chain-name">{CHAIN_TO_CHAIN_NAME[destinationChain]}</span>
                      <span className="usdc-badge">USDC</span>
                    </div>
                    <FaChevronDown />
                  </button>
                </div>
              </div>

              {/* Transfer Speed Options - Show for all chains */}
              <div className="transfer-speed-selector">
                <label className="speed-label">Transfer Speed</label>
                <div className="speed-options">
                  <button
                    className={`speed-option ${transferSpeed === 'standard' ? 'active' : ''}`}
                    onClick={() => setTransferSpeed('standard')}
                    disabled={isTransferring}
                  >
                    <div className="speed-info">
                      <span className="speed-name">Standard</span>
                      <span className="speed-fee">Free</span>
                    </div>
                    <span className="speed-time">{getEstimatedTime(sourceChain, false)}</span>
                  </button>
                  <button
                    className={`speed-option ${!fastTransferSupported ? 'unavailable' : ''} ${transferSpeed === 'fast' && fastTransferSupported ? 'active' : ''}`}
                    onClick={() => fastTransferSupported && setTransferSpeed('fast')}
                    disabled={isTransferring || !fastTransferSupported}
                    title={!fastTransferSupported ? `${CHAIN_TO_CHAIN_NAME[sourceChain]} already processes transfers at maximum speed` : ''}
                  >
                    <div className="speed-info">
                      <span className="speed-name">Fast</span>
                      <span className="speed-fee">
                        {fastTransferSupported 
                          ? `${CCTPFees.fast[sourceChain] / 100}%`
                          : 'N/A'
                        }
                      </span>
                    </div>
                    <span className="speed-time">
                      {fastTransferSupported 
                        ? getEstimatedTime(sourceChain, true)
                        : 'Already fast'
                      }
                    </span>
                  </button>
                </div>
              </div>

              {/* Transfer Details */}
              {amount && parseFloat(amount) > 0 && feeCalculation && (
                <div className="bridge-details">
                  <div className="detail-row">
                    <span>Circle Fee</span>
                    <span>{feeCalculation.circleFeeRate > 0 
                      ? `${(parseFloat(feeCalculation.circleFee) / 1e6).toFixed(4)} USDC (${feeCalculation.circleFeeRate / 100}%)` 
                      : 'Free'
                    }</span>
                  </div>
                  <div className="detail-row">
                    <span>Platform Fee</span>
                    <span>{feeCalculation.platformFeeRate > 0 
                      ? `${(parseFloat(feeCalculation.platformFee) / 1e6).toFixed(4)} USDC (${feeCalculation.platformFeeRate / 100}%)` 
                      : 'Free'
                    }</span>
                  </div>
                  <div className="detail-row">
                    <span>Estimated Time</span>
                    <span>{getEstimatedTime(sourceChain, feeCalculation.isFastTransfer)}</span>
                  </div>
                  <div className="detail-row total-row">
                    <span>You will receive</span>
                    <span className="highlight">{formatAmountNoTrailingZeros((parseFloat(feeCalculation.amountAfterAllFees) / 1e6).toFixed(6))} USDC</span>
                  </div>
                </div>
              )}

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <button 
                className="bridge-button"
                onClick={handleTransfer}
                disabled={isButtonDisabled}
              >
                {!isConnected ? 'Connect Wallet' :
                 isTransferring ? 'Processing...' :
                 !amount || parseFloat(amount) <= 0 ? 'Enter Amount' :
                 parseFloat(amount) > parseFloat(sourceBalance) ? 'Insufficient Balance' :
                 'Bridge USDC'}
              </button>
            </div>

            {transferStep !== TRANSFER_STEPS.IDLE && (
              <motion.div 
                className="progress-section"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
              >
                <TransferProgress 
                  currentStep={transferStep}
                  txHashes={txHashes}
                  destinationChain={destinationChain}
                />
              </motion.div>
            )}
          </div>

          {/* Gas refuel info */}
          <div className="gas-refuel-info">
            <div className="info-content">
              <span className="info-icon-wrapper">
                <FaInfoCircle />
              </span>
              <span className="info-text">
                Need gas tokens on a new chain? Visit{' '}
                <a 
                  href="https://smolrefuel.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="refuel-link"
                >
                  SmolRefuel.com
                </a>
                {' '}for gas refueling on any chain. Built by DefiLlama developers.
              </span>
            </div>
          </div>
        </div>

        <div className="bridge-sidebar">
          <TransactionHistory 
            currentTransaction={
              txHashes.burn ? {
                sourceChain,
                destinationChain,
                amount,
                burnHash: txHashes.burn,
                mintHash: txHashes.mint,
                status: transferStep === TRANSFER_STEPS.COMPLETED ? 'completed' : 
                        transferStep === TRANSFER_STEPS.ERROR ? 'failed' :
                        transferStep === TRANSFER_STEPS.WAITING_ATTESTATION ? 'attestation_pending' :
                        transferStep === TRANSFER_STEPS.READY_TO_MINT ? 'attestation_ready' :
                        'pending',
              } : null
            }
            onMintComplete={handleMintComplete}
          />
          
          <div className="info-card">
            <h3>About CCTP</h3>
            <p>
              Circle's Cross-Chain Transfer Protocol (CCTP) enables secure USDC transfers between blockchains. 
              Transfers are native - no wrapped tokens required.
            </p>
            <ul>
              <li>✓ 1:1 USDC transfers</li>
              <li>✓ No slippage</li>
              <li>✓ Burn & mint mechanism</li>
              <li>✓ Secured by Circle</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Chain Select Modal */}
      <ChainSelectModal
        isOpen={showChainModal}
        onClose={closeChainModal}
        onSelectChain={handleChainSelect}
        modalType={chainModalType}
        currentChain={chainModalType === 'source' ? sourceChain : destinationChain}
        oppositeChain={chainModalType === 'source' ? destinationChain : sourceChain}
        userAddress={address}
      />
    </motion.div>
  );
};

export default Bridge;